/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { CosmosText } from "@components/elements";
import { VStack } from "@panda/jsx";
import { useCurrentSearchQuery, useDirectoryContext } from "../core/Directory.context";

export type DirectoryNoResultsProps = {
	/**
	 * Render prop receives the current search query (or "" if none). Use this to show
	 * "No results for 'foo'" style messages. If omitted, falls back to the Directory's
	 * labels props and then to the English default.
	 */
	children?: (query: string) => React.ReactNode;
};

/**
 * Empty state slot. The visible "no results" message — NO live-region attrs
 * here. The `Directory.molecule` sr-only status region is the single source
 * of truth for filter-change announcements (avoids a duplicate announcement
 * race when results drop to zero).
 *
 * Consumers can pass a render prop for domain-specific empty-state copy.
 */
export function DirectoryNoResults({ children }: DirectoryNoResultsProps = {}): React.JSX.Element {
	const { config } = useDirectoryContext();
	const query = useCurrentSearchQuery();

	let label: React.ReactNode;
	if (children) {
		label = children(query);
	} else if (query && config.noResultsWithQueryLabel) {
		label = config.noResultsWithQueryLabel(query);
	} else if (query) {
		label = `No results for "${query}"`;
	} else {
		label = config.noResultsLabel ?? "No results";
	}

	return (
		<VStack paddingBlock="eds.dimension.48" alignItems="center">
			<CosmosText variant="paragraphLG" color="eds.text.secondary">
				{label}
			</CosmosText>
		</VStack>
	);
}
